import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createSuggestion,
  getProduct,
  getProductStatus,
  updateProduct,
} from "../api";
import {
  Affix,
  Anchor,
  Breadcrumbs,
  Button,
  Center,
  Code,
  Container,
  Group,
  Loader,
  Switch,
  Table,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconDeviceFloppy, IconWand } from "@tabler/icons-react";
import ProductAttributeRow from "./Details/ProductAttributeRow";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import ProductStatusBadge from "./ProductStatusBadge";

function ProductHierarchy({ hierarchy, marketplaceId, skipRoot = true }) {
  return (
    <Breadcrumbs separator="→" separatorMargin="xs" mt="xs">
      {hierarchy.levels.slice(skipRoot ? 1 : 0).map((item) => (
        <Anchor
          component={Link}
          key={item.id}
          to={`/marketplaces/${marketplaceId}/types/${item.id}`}
        >
          {item.label} <Code>{item.code}</Code>
        </Anchor>
      ))}
    </Breadcrumbs>
  );
}

export default function Details({ params }) {
  const { t } = useTranslation("pim");
  const [product, setProduct] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isMapping, setIsMapping] = useState(false);
  const [allFieldValues, setAllFieldValues] = useState([]);
  const pollingIntervalRef = useRef(null); // Use ref to store interval ID

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {},
  });

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const setProductAndForm = (data) => {
    setProduct(data);
    let allValues = [];

    let initialValues = {};
    for (let i = 0; i < data.hydratedType.attributes.length; i++) {
      const attrib = data.hydratedType.attributes[i];
      const attribValue = data.fieldValues[attrib.code]?.value;
      initialValues[attrib.code] = attribValue;
      allValues.push(attrib);
    }
    for (const field in data.fieldValues) {
      const fieldValue = data.fieldValues[field];
      if (!initialValues.hasOwnProperty(fieldValue.code)) {
        initialValues[fieldValue.code] = fieldValue.value;
        allValues.push({
          code: fieldValue.code,
          label: fieldValue.code,
          type: "Undefined",
        });
      }
    }

    // console.warn("SET FORM IN", data);
    // console.warn("SET FORM OUT", allValues);

    // var valuesPrev = form.getValues();
    // const areEq = shallowEqual(valuesPrev, initialValues);
    // console.log("areEq", areEq);

    form.setValues(initialValues);
    form.setInitialValues(initialValues);
    setAllFieldValues(allValues);
    form.setDirty(false);
  };

  useEffect(() => {
    getProduct(params.id).then((data) => {
      setProductAndForm(data);
    });
  }, [params.id]);

  const attributes = useMemo(() => {
    if (!product) return [];
    const filterValue = filter.toLowerCase().trim();
    const shouldFilter = !showAll && filterValue.length > 0;
    const validationFailures = product.validationFailures || [];
    const fieldValues = product.fieldValues || {};

    const base = allFieldValues.filter((attrib) => {
      if (showAll) return true;

      const attribValue = fieldValues[attrib.code]?.value;

      if (shouldFilter) {
        return (
          attrib.label?.toLowerCase().includes(filterValue) ||
          attrib.code?.toLowerCase().includes(filterValue) ||
          attrib.description?.toLowerCase().includes(filterValue) ||
          attribValue?.toLowerCase().includes(filterValue)
        );
      }

      const hasValidationFailure = validationFailures.some(
        (p) => p.field === attrib.code
      );
      const hasValue = Boolean(attribValue);

      return (
        attrib.requirementLevel === "REQUIRED" ||
        hasValue ||
        hasValidationFailure
      );
    });

    return base.sort((a, b) => {
      const validationFailuresA = validationFailures.some(
        (p) => p.field === a.code
      );
      const validationFailuresB = validationFailures.some(
        (p) => p.field === b.code
      );
      if (validationFailuresA && !validationFailuresB) return -1;
      if (!validationFailuresA && validationFailuresB) return 1;

      if (a.roles && b.roles && a.roles.length !== b.roles.length)
        return b.roles.length - a.roles.length;
      if (a.hierarchyDepth !== b.hierarchyDepth)
        return a.hierarchyDepth - b.hierarchyDepth;
      return a.code.toLowerCase().localeCompare(b.code.toLowerCase());
    });
  }, [allFieldValues, filter, product, showAll]);

  const formValues = form.getValues() || {};
  const formErrors = form.errors || {};

  const handleDeleteAttribute = useCallback(
    (code) => {
      setAllFieldValues((prev) => prev.filter((a) => a.code !== code));
      form.setDirty({ [code]: true });
    },
    [form]
  );

  if (!product)
    return (
      <Container size="xs">
        <Center>
          <Loader mt="xl" size="xl" />
        </Center>
      </Container>
    );

  const hasOriginalValues =
    product.originalValues &&
    typeof product.originalValues === "object" &&
    Object.values(product.originalValues).some((v) => v !== "" && v != null);

  return (
    <Container fluid={hasOriginalValues} size={hasOriginalValues ? null : "lg"}>
      <Group justify="space-between" wrap="nowrap">
        <Anchor onClick={() => window.history.back()} c="gray">
          {t("products.back")}
        </Anchor>
        <Group>
          {[...new Set(product.status.split(","))].map((flag) => (
            <ProductStatusBadge
              key={product.id + "status" + flag}
              status={flag.trim()}
            />
          ))}
        </Group>
      </Group>

      <Title order={1}>{product.fieldValues?.name?.value}</Title>

      <ProductHierarchy
        marketplaceId={product.marketplaceId}
        hierarchy={product.hierarchy}
      />

      <div
        style={
          hasOriginalValues
            ? { width: "100%" }
            : { width: "60%", margin: "0 auto" }
        }
      >
        <Group align="flex-end" my="sm" justify="space-between">
          <Group align="flex-start">
            <TextInput
              label={t("legacyProducts.filter")}
              disabled={showAll}
              value={filter}
              onChange={(event) => setFilter(event.currentTarget.value)}
            />
          </Group>
        </Group>

        <form
          onSubmit={form.onSubmit((values) => console.log("Submit", values))}
        >
          <Affix position={{ bottom: 20, right: 20 }}>
            <Group>
              <Button
                type="reset"
                display={form.isDirty() === false ? "none" : "block"}
                disabled={form.isDirty() === false}
                variant="default"
                onClick={() => form.reset()}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                loading={isSaving}
                display={form.isDirty() === false ? "none" : "block"}
                disabled={form.isDirty() === false}
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={() => {
                  setIsSaving(true);
                  const formValues = form.getValues();
                  let formValuesStr = {};
                  Object.keys(formValues).forEach((key) => {
                    const value = formValues[key];
                    // Skip null/undefined/empty string values (retain previous behavior of excluding falsy except 0/false)
                    if (value === null || value === undefined || value === "")
                      return;

                    if (Array.isArray(value)) {
                      // Allow arrays of (string|number|boolean) by coercing each element to string and filtering empties
                      const arr = value
                        .filter(
                          (v) => v !== null && v !== undefined && v !== ""
                        )
                        .map((v) => (typeof v === "string" ? v : String(v)));
                      if (arr.length > 0) formValuesStr[key] = arr; // preserve as array
                    } else if (
                      typeof value === "number" ||
                      typeof value === "boolean" ||
                      value instanceof Number ||
                      value instanceof Boolean
                    ) {
                      formValuesStr[key] = String(value);
                    } else if (typeof value === "string") {
                      formValuesStr[key] = value; // already string
                    } else {
                      // Fallback: attempt to stringify other primitives (e.g., Date) while avoiding [object Object]
                      if (
                        value instanceof Date ||
                        typeof value.toString === "function"
                      ) {
                        const str = value.toString();
                        if (str && str !== "[object Object]")
                          formValuesStr[key] = str;
                      }
                    }
                  });

                  updateProduct(product.id, {
                    fieldValues: formValuesStr,
                  }).then((data) => {
                    notifications.show({ title: t("grid.saved") });
                    // setProduct(data);
                    setProductAndForm(data);
                    setIsSaving(false);
                  });
                }}
              >
                {t("common.save")}
              </Button>

              <Tooltip label="TBD">
                <Button
                  display={form.isDirty() === true ? "none" : "block"}
                  loading={isMapping}
                  disabled={product.status.includes("mapped")}
                  leftSection={<IconWand size={16} />}
                  onClick={() => {
                    setIsMapping(true);
                    setProduct({
                      ...product,
                      status: product.status + ",processing",
                    });
                    createSuggestion(product.id)
                      .then(() => {
                        // Clear any existing interval before starting a new one
                        if (pollingIntervalRef.current) {
                          clearInterval(pollingIntervalRef.current);
                        }
                        // Start polling
                        pollingIntervalRef.current = setInterval(() => {
                          getProductStatus(product.id)
                            .then((statusData) => {
                              const status = statusData.status;
                              // Check if status includes 'failed' or 'mapped' and no longer 'processing'
                              const isProcessing =
                                status.includes("processing");
                              const isFinished =
                                status.includes("failed") ||
                                status.includes("mapped");

                              if (isFinished && !isProcessing) {
                                clearInterval(pollingIntervalRef.current);
                                pollingIntervalRef.current = null; // Clear ref

                                getProduct(product.id)
                                  .then((updatedProductData) => {
                                    setProductAndForm(updatedProductData);
                                    setIsMapping(false);
                                    notifications.show({
                                      title: status.includes("failed")
                                        ? t("legacyProducts.mappingFailed")
                                        : t("legacyProducts.mappingComplete"),
                                    });
                                  })
                                  .catch((error) => {
                                    console.error(
                                      "Failed to fetch product after polling:",
                                      error
                                    );
                                    setIsMapping(false); // Still stop loading indicator
                                    notifications.show({
                                      title: t(
                                        "legacyProducts.errorFetchingProduct"
                                      ),
                                      message: error.message,
                                      color: "red",
                                    });
                                    // Optionally fetch product again to get latest state even on error
                                    getProduct(product.id)
                                      .then(setProductAndForm)
                                      .catch(console.error);
                                  });
                              } else if (!isProcessing) {
                                // If it's not processing anymore but also not failed/mapped (unexpected state)
                                console.warn(
                                  "Polling stopped due to unexpected status:",
                                  status
                                );
                                clearInterval(pollingIntervalRef.current);
                                pollingIntervalRef.current = null;
                                // Fetch the latest product data to reflect the current state
                                getProduct(product.id)
                                  .then(setProductAndForm)
                                  .catch(console.error);
                                setIsMapping(false);
                              }
                              // else: still processing, continue polling
                            })
                            .catch((error) => {
                              console.error(
                                "Error during status polling:",
                                error
                              );
                              // Stop polling on error to avoid infinite loops if endpoint is broken
                              clearInterval(pollingIntervalRef.current);
                              pollingIntervalRef.current = null;
                              setIsMapping(false);
                              notifications.show({
                                title: t("legacyProducts.pollingErrorTitle"),
                                message: t(
                                  "legacyProducts.pollingErrorMessage"
                                ),
                                color: "red",
                              });
                              // Fetch the latest product data to reflect the current state
                              getProduct(product.id)
                                .then(setProductAndForm)
                                .catch(console.error);
                            });
                        }, 5000); // Poll every 5 seconds
                      })
                      .catch((error) => {
                        console.error("Failed to create suggestion:", error);
                        setIsMapping(false); // Reset loading state
                        // Fetch product again to revert optimistic status update and get actual status
                        getProduct(product.id)
                          .then(setProductAndForm)
                          .catch(console.error);
                        notifications.show({
                          title: t("legacyProducts.mappingErrorTitle"),
                          message: error.message,
                          color: "red",
                        });
                      });
                  }}
                >
                  {t("legacyProducts.map")}
                </Button>
              </Tooltip>
            </Group>
          </Affix>

          <Table
            mb={100}
            stickyHeader
            stickyHeaderOffset={58}
            mt="xl"
            variant="vertical"
            withTableBorder
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Group gap="lg">
                    {t("products.attribute")}
                    <Switch
                      label={t("legacyProducts.showAll")}
                      checked={showAll}
                      onChange={(event) =>
                        setShowAll(event.currentTarget.checked)
                      }
                    />
                  </Group>
                </Table.Th>
                <Table.Th style={hasOriginalValues ? { width: "40%" } : null}>
                  {t("legacyProducts.currentContent")}
                </Table.Th>
                {hasOriginalValues && (
                  <Table.Th style={{ width: "40%" }}>
                    {t("legacyProducts.originalContent")}
                  </Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {attributes.map((attrib) => (
                <ProductAttributeRow
                  showOriginalValue={hasOriginalValues}
                  form={form}
                  key={attrib.code}
                  product={product}
                  attrib={attrib}
                  fieldValue={formValues?.[attrib.code]}
                  fieldError={formErrors?.[attrib.code]}
                  onDeleteAttribute={handleDeleteAttribute}
                />
              ))}
            </Table.Tbody>
          </Table>
        </form>
      </div>
    </Container>
  );
}
